import { Component, Input } from '@angular/core';
import { PresentationConfig, PresentationChapter } from '../includes/PresentationConfig';
import { MenuComponent } from './Menu';
import { Slide } from '../includes/Slide';
import { SlideInfo } from '../includes/SlideInfo';
import { ChapterSlide } from '../includes/ChapterSlide';
import { SLIDE_FOLDER } from '../includes/Settings';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

@Component({
  selector: 'presentation',
  templateUrl: './Presentation.html',
})
export class PresentationComponent {

  @Input() key: string;
  _config: PresentationConfig = null;
  currentSlide = 0;
  previousSlide = 0;
  slides: SlideInfo[] = [];

  constructor(protected readonly _sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    fetch(SLIDE_FOLDER + '/config.json')
      .then((response) => response.json())
      .then((json) => this.readConfig(json));
  }

  private readConfig(json: PresentationConfig): void {
    this._config = {
      ...{
        animation: {
          slide: {
            in: ['none-in'],
            out: ['none-out'],
            duration: 0,
          },
          chapter: {
            in: ['none-in'],
            out: ['none-out'],
            duration: 0,
          }
        }
      },
      ...json,
      ...json.presentations.filter((e: PresentationConfig) => e.key === this.key).pop()
    };
    fetch(SLIDE_FOLDER + '/' + this._config.key + '/config.json')
      .then((response) => response.json())
      .then((json) => {
        this._config = { ...this._config, ...json, loaded: true };
        this.loadSlides();
      });

  }

  public get config(): PresentationConfig {
    if (!this._config || !this._config.loaded) {
      return null;
    }
    return this._config;
  }

  public get styles(): SafeStyle[] {
    return (this._config?.styles ?? []).map((s) =>
      this._sanitizer.bypassSecurityTrustResourceUrl(SLIDE_FOLDER + '/' + this._config.key + '/' + s)
    );
  }

  public getAnimationName(info: SlideInfo): string {
    if (this.previousSlide <= this.currentSlide) {
      //movin forward
      return info.order === this.currentSlide ? info.animation.in : info.animation.out;
    } else {
      //movin backwards
      return info.order === this.currentSlide ? info.animation.out : info.animation.in;
    }
  }

  public getAnimationDirection(info: SlideInfo): string {
    if (this.previousSlide > this.currentSlide) {
      return 'reverse';
    }
    return 'normal';
  }

  private loadSlides(): void {
    let currentChapter = -1;
    let slideIndexOffset = 0;
    this.config.chapters.forEach((chapter: PresentationChapter, chapterIndex: number) => {
      chapter.slides.forEach((file: string) => {
        const animationIndex = this.slides.length + slideIndexOffset;
        let animationIn = this.config.animation.slide.in[animationIndex % this.config.animation.slide.in.length];
        let animationOut = this.config.animation.slide.out[animationIndex % this.config.animation.slide.out.length];
        let animationDuration = this.config.animation.slide.duration;
        if (currentChapter !== chapterIndex) {
          const animationChapterIn = this.config.animation.chapter.in[chapterIndex % this.config.animation.chapter.in.length];
          const animationChapterOut = this.config.animation.chapter.out[chapterIndex % this.config.animation.chapter.out.length];
          const animationChapterDuration = this.config.animation.chapter.duration;
          if (chapter.title) {
            this.slides.push({
              order: this.slides.length,
              chapter: chapterIndex,
              slide: new ChapterSlide(chapter.title),
              animation: {
                in: animationChapterIn,
                out: animationChapterOut,
                duration: animationChapterDuration + 's',
              }
            });
            slideIndexOffset--;
          } else {
            animationIn = animationChapterIn;
            animationOut = animationChapterOut;
            animationDuration = animationChapterDuration;
          }
          currentChapter = chapterIndex;
        }
        this.slides.push({
          order: this.slides.length,
          chapter: chapterIndex,
          slide: new Slide(this.config.key + '/' + file),
          animation: {
            in: animationIn,
            out: animationOut,
            duration: animationDuration + 's',
          }
        });
      });
    });
  }

  public onKeyup(event: any): void {
    switch(event.code) {
      case "Space":
      case "ArrowRight":
        this.onNextSlide();
        break;
      case "ArrowLeft":
        this.onPreviousSlide();
        break;
      case "ArrowUp":
        this.onPreviousChapter();
        break;
      case "ArrowDown":
        this.onNextChapter();
        break;
      case "Escape":
        this.onClose();
        break;
      default:
        console.log('unknown key event ' + event.code);
    }
  }

  public onPreviousSlide(): void {
    this.previousSlide = this.currentSlide;
    this.currentSlide = Math.max(0, this.currentSlide - 1);
  }

  public onNextSlide(): void {
    this.previousSlide = this.currentSlide;
    this.currentSlide = Math.min(this.slides.length - 1, this.currentSlide + 1);
  }

  public onPreviousChapter(): void {
    this.previousSlide = this.currentSlide;
    const thisChapter = Math.max(0, this.slides[this.currentSlide].chapter);
    this.currentSlide = this.slides.filter((slide: any) => slide.chapter === thisChapter)[0].order;
    if (this.currentSlide === this.previousSlide) {
      const prev = Math.max(0, this.slides[this.currentSlide].chapter - 1);
      this.currentSlide = this.slides.filter((slide: any) => slide.chapter === prev)[0].order;
    }
  }

  public onNextChapter(): void {
    this.previousSlide = this.currentSlide;
    const maxChapter = this.slides[this.slides.length - 1].chapter;
    const thisChapter = this.slides[this.currentSlide].chapter;
    const next = Math.min(maxChapter, thisChapter + 1);
    this.currentSlide = this.slides.filter((slide: any) => slide.chapter === next)[0].order;
  }

  public onClose(): void {
    MenuComponent.selectedPresentation = null;
  }
}
